import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { formatBytes, formatCount, formatDay } from "../lib/format";
import type { AdminUserUsage } from "../lib/types";

/**
 * Instance health, for whoever runs it.
 *
 * Reachable only with a Steam ID listed in `ADMIN_STEAM_IDS`; everyone else gets a
 * 404 from the API and no link in the nav. Written to answer the questions that
 * matter on a fixed-size volume: who is using the space, is the database keeping up
 * with the blobs, and is anything failing to ingest.
 */
export function AdminPage() {
  const { t } = useTranslation();
  const overview = useQuery({
    queryKey: ["admin-overview"],
    queryFn: api.adminOverview,
    refetchInterval: 30_000,
  });

  if (overview.isPending) {
    return <p className="text-sm text-zinc-500">{t("admin.loading")}</p>;
  }
  if (overview.isError || !overview.data) {
    return <p className="text-sm text-red-400">{t("admin.error")}</p>;
  }

  const data = overview.data;
  const { totals, storage, ingest } = data;
  const unclaimed = storage.unclaimed_bytes > 5_000_000;

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{t("admin.title")}</h1>
      <p className="mt-1 text-sm text-zinc-500">{t("admin.subtitle")}</p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label={t("admin.tiles.drivers")}
          value={formatCount(totals.users)}
          detail={t("admin.tiles.driversDetail", { count: totals.signups_last_week })}
        />
        <Tile
          label={t("admin.tiles.sessions")}
          value={formatCount(totals.sessions)}
          detail={t("admin.tiles.sessionsDetail", {
            ready: totals.ready_sessions,
            today: totals.sessions_last_day,
          })}
        />
        <Tile
          label={t("admin.tiles.traces")}
          value={formatBytes(storage.total_bytes)}
          detail={t("admin.tiles.tracesDetail", {
            count: storage.total_files,
            root: storage.root,
          })}
        />
        <Tile
          label={t("admin.tiles.database")}
          value={formatBytes(data.database_bytes)}
          detail={t("admin.tiles.databaseDetail", {
            laps: formatCount(totals.laps),
            teams: totals.teams,
          })}
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label={t("admin.tiles.recorded")}
          value={formatBytes(totals.uploaded_bytes)}
          detail={t("admin.tiles.recordedDetail", {
            kept: formatBytes(totals.trace_bytes),
            ratio:
              totals.uploaded_bytes && totals.trace_bytes
                ? t("admin.tiles.ratio", {
                    factor: Math.round(totals.uploaded_bytes / totals.trace_bytes),
                  })
                : "—",
          })}
        />
        <Tile
          label={t("admin.tiles.ingest")}
          value={
            ingest.median_seconds === null
              ? "—"
              : `${ingest.median_seconds.toFixed(1)} s`
          }
          detail={
            ingest.slowest_seconds === null
              ? t("admin.tiles.ingestNone")
              : t("admin.tiles.ingestDetail", {
                  count: ingest.counted,
                  worst: Math.round(ingest.slowest_seconds),
                })
          }
        />
        <Tile
          label={t("admin.tiles.private")}
          value={formatCount(totals.private_sessions)}
          detail={t("admin.tiles.privateDetail", { count: totals.sessions })}
        />
        <Tile
          label={t("admin.tiles.unclaimed")}
          value={formatBytes(Math.max(0, storage.unclaimed_bytes))}
          detail={t(unclaimed ? "admin.tiles.unclaimedWarn" : "admin.tiles.unclaimedOk")}
          warn={unclaimed}
        />
      </div>

      {data.statuses.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {data.statuses.map((entry) => (
            <span
              key={entry.status}
              className={[
                "rounded-full border px-3 py-1 text-xs",
                entry.status === "failed"
                  ? "border-red-900/70 text-red-300"
                  : "border-zinc-700 text-zinc-400",
              ].join(" ")}
            >
              {t(`status.${entry.status}`, { defaultValue: entry.status })}:{" "}
              <span className="tabular-nums">{entry.count}</span>
            </span>
          ))}
        </div>
      )}

      <Section title={t("admin.drivers")} hint={t("admin.driversHint")}>
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">{t("admin.columns.driver")}</th>
              <th className="px-3 py-2 text-right font-medium">
                {t("admin.columns.sessions")}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t("admin.columns.laps")}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t("admin.columns.traces")}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t("admin.columns.uploaded")}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t("admin.columns.teams")}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t("admin.columns.lastUpload")}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t("admin.columns.lastSeen")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {data.users.map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
          </tbody>
        </table>
      </Section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section title={t("admin.tables")} hint={t("admin.tablesHint")}>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-zinc-800">
              {data.tables.map((table) => (
                <tr key={table.name}>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-300">
                    {table.name}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-500">
                    {table.row_estimate > 0
                      ? t("admin.rows", { count: table.row_estimate })
                      : t("admin.notAnalysed")}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-300">
                    {formatBytes(table.total_bytes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title={t("admin.storage")} hint={storage.root}>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-zinc-800">
              {storage.directories.length === 0 ? (
                <tr>
                  <td className="px-3 py-2 text-sm text-zinc-500">
                    {t("admin.storageEmpty")}
                  </td>
                </tr>
              ) : (
                storage.directories.map((directory) => (
                  <tr key={directory.name}>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-300">
                      {directory.name}/
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-500">
                      {t("admin.files", { count: directory.files })}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-zinc-300">
                      {formatBytes(directory.bytes)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}

function UserRow({ user }: { user: AdminUserUsage }) {
  const { t } = useTranslation();

  return (
    <tr className="hover:bg-zinc-900/40">
      <td className="px-3 py-2">
        <span className="flex items-center gap-2">
          {user.avatar_url && (
            <img
              src={user.avatar_url}
              alt=""
              className="size-6 rounded-full ring-1 ring-zinc-700"
            />
          )}
          <span>
            <span className="block text-zinc-200">{user.persona_name}</span>
            <span className="block font-mono text-[10px] text-zinc-600">
              {user.steam_id64}
              {user.country_code && ` · ${user.country_code}`}
            </span>
          </span>
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-300">
        {user.sessions}
        {user.failed_sessions > 0 && (
          <span className="ml-1 text-xs text-red-400">
            {t("admin.failedCount", { count: user.failed_sessions })}
          </span>
        )}
        {user.private_sessions > 0 && (
          <span className="ml-1 text-xs text-zinc-600">
            {t("admin.privateCount", { count: user.private_sessions })}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{user.laps}</td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-200">
        {formatBytes(user.trace_bytes)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">
        {formatBytes(user.uploaded_bytes)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{user.teams}</td>
      <td className="px-3 py-2 text-right text-xs text-zinc-500">
        {formatDay(user.last_upload_at)}
      </td>
      <td className="px-3 py-2 text-right text-xs text-zinc-500">
        {formatDay(user.last_login_at)}
      </td>
    </tr>
  );
}

interface TileProps {
  label: string;
  value: string;
  detail: string;
  warn?: boolean;
}

function Tile({ label, value, detail, warn }: TileProps) {
  return (
    <div
      className={[
        "rounded-xl border px-4 py-3",
        warn ? "border-amber-700/60 bg-amber-500/[0.04]" : "border-zinc-800",
      ].join(" ")}
    >
      <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-zinc-100">{value}</p>
      <p className="mt-0.5 text-xs text-zinc-500">{detail}</p>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-8">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-zinc-500">
          {title}
        </h2>
        {hint && <span className="text-xs text-zinc-600">{hint}</span>}
      </div>
      <div className="mt-3 overflow-x-auto rounded-xl border border-zinc-800">
        {children}
      </div>
    </div>
  );
}

